import { useEffect, useRef, useState, type RefObject } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Hls from 'hls.js';
import { api, ApiError } from '@/lib/api';
import { cn } from '@/lib/cn';
import type { Episode } from '@/lib/contract';
import { AnnotationTimeline, useEpisodeTimeline } from '@/domain/annotation-timeline';
import { Panel } from '@/ui/panel';
import { Button } from '@/ui/button';
import { Badge } from '@/ui/badge';
import { MicroLabel } from '@/ui/micro-label';
import { ErrorState } from '@/ui/error-state';

/**
 * The episode viewer, playing the actual video.
 *
 * What stood here was a frame: a play glyph, and five badges reading RGB / Audio / Pose / Segments /
 * Depth that were the same five whatever the dataset contained. The cameras below are the ones
 * `meta/info.json` declared, named as the vendor named them, and the resolution and codec beside
 * each are what that file states.
 *
 * Nothing here is generated in advance. A preview costs an encode per camera per episode, and a
 * batch carries hundreds, so the first visit to a camera offers the button and the transcode
 * happens because someone asked.
 */

type Media = {
  video_key: string;
  source_codec: string | null;
  fps: number | null;
  width: number | null;
  height: number | null;
  preview_state: 'none' | 'queued' | 'running' | 'ready' | 'failed';
  error_code: string | null;
  uploaded: boolean;
  has_poster: boolean;
  /**
   * Whether the uploaded object plays in a browser as it stands — H.264, and this episode alone.
   * When it does, there is nothing to transcode and the viewer streams the original.
   */
  direct_playable: boolean;
};

/** Which rendition the viewer is showing. `original` needs no transcode and is the default. */
type Variant = 'original' | 'preview';

/**
 * The camera's own name, minus the prefix every LeRobot key shares.
 *
 * An MCAP camera is named after its topic, so it arrives with a leading slash: `/camera`,
 * `/feature-tracks`. Dropping it reads as a name rather than a path, which is what the pill is.
 */
const cameraLabel = (key: string) =>
  key
    .replace(/^.*observation\.images?\./, '')
    .replace(/^\//, '')
    .replaceAll('_', ' ');

/** A signature expiring mid-playback is worth retrying; an undecodable file is not worth 100. */
const MAX_RESIGNS = 3;

const STATE_TEXT: Record<Media['preview_state'], string> = {
  none: 'No preview generated yet',
  queued: 'Queued · waiting for a transcoder',
  running: 'Transcoding…',
  ready: '',
  failed: 'The transcode failed'
};

export function EpisodeViewer({
  datasetId,
  tenantId,
  episode
}: {
  datasetId: string;
  tenantId?: string;
  episode?: Episode;
}) {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const episodeId = episode?.id;
  /**
   * Where the playhead is, in milliseconds since the episode began, and the element to move it.
   *
   * Held here rather than inside the player because the annotation strip is a sibling of the video,
   * not a child of it — the position has to be readable by both. The element itself is shared by
   * ref so a click on the strip can seek without a second state round-trip.
   */
  const videoRef = useRef<HTMLVideoElement>(null);
  const [positionMs, setPositionMs] = useState(0);
  /**
   * Whether it was playing, and where, at the moment the player was replaced.
   *
   * Switching camera destroys the video element and builds another, which used to mean going back to
   * 00:00 paused every time — so comparing two views of the same instant, which is the entire reason
   * for having more than one camera, meant seeking twice and pressing play twice. Both facts live
   * above the player so they survive it.
   */
  const [playing, setPlaying] = useState(false);
  const [variant, setVariant] = useState<Variant | null>(null);
  const annotations = useEpisodeTimeline(datasetId, episodeId, tenantId);
  const timeline = annotations.data ?? null;

  /**
   * A different episode is a different recording: back to the start, and paused.
   *
   * Done here, during render, and not in an effect. An effect runs *after* the commit, and the
   * player can mount within that same commit — react-query hands back a cached media list
   * synchronously for an episode already visited — so the new player read the previous episode's
   * position and play state and carried them over. Resetting during render means the values the
   * player mounts with are already the new episode's.
   *
   * This is React's documented way to adjust state when a prop changes: guarded by a comparison, so
   * it runs once per change rather than every render.
   */
  const shownEpisode = useRef(episodeId);
  if (shownEpisode.current !== episodeId) {
    shownEpisode.current = episodeId;
    setPositionMs(0);
    setPlaying(false);
  }

  const media = useQuery({
    queryKey: ['admin', 'episode-media', datasetId, episodeId],
    enabled: !!episodeId,
    queryFn: () => api<Media[]>(`/datasets/${datasetId}/episodes/${episodeId}/media`, { tenantId }),
    /**
     * Polled only while a transcode is actually in flight. The `media` lane publishes nothing when
     * it finishes -- a preview is not a pipeline stage and has no place in the run's event stream --
     * so this is what notices, and it stops the moment nothing is moving.
     */
    refetchInterval: query =>
      query.state.data?.some(m => m.preview_state === 'queued' || m.preview_state === 'running') ? 3000 : false
  });

  const request = useMutation({
    mutationFn: () => api(`/datasets/${datasetId}/episodes/${episodeId}/preview`, { method: 'POST', tenantId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'episode-media', datasetId, episodeId] })
  });

  const cameras = media.data ?? [];
  const current = cameras.find(c => c.video_key === selected) ?? cameras.find(c => c.uploaded) ?? cameras[0];

  /**
   * Which rendition to play, and whether there is a choice.
   *
   * The original wins by default whenever it can: an H.264 object is already what the transcode was
   * trying to produce, so re-encoding it costs minutes of CPU and a second copy of the bytes to
   * arrive back where it started. The preview stays reachable for a slow link, and is the only
   * option for a source no browser decodes or an episode that has to be cut out of a larger file.
   *
   * `variant` is null until someone chooses, so the default follows the camera rather than sticking
   * to whatever the last one happened to support.
   */
  const canPlayOriginal = !!current?.direct_playable;
  const canPlayPreview = current?.preview_state === 'ready';
  const showing: Variant | null = !current
    ? null
    : variant === 'preview' && canPlayPreview
      ? 'preview'
      : variant === 'original' && canPlayOriginal
        ? 'original'
        : canPlayOriginal
          ? 'original'
          : canPlayPreview
            ? 'preview'
            : null;

  return (
    <Panel
      title={
        episode ? (
          <span className="font-mono text-[13px]">
            {/* The L1 caption when the dataset has one: it is the episode's own description of its
                goal, which is a better title than the task label the table already shows. */}
            {episode.code} · {timeline?.caption ?? episode.task?.split(' · ')[0] ?? '—'}
          </span>
        ) : (
          'Episode viewer'
        )
      }
      action={
        /**
         * The transcode is now optional for some cameras and required for others, so the button has
         * to say which. When the original already plays, a preview only buys a smaller rendition for
         * a slow link — offering that as "Generate preview" beside working video reads like
         * something is missing. When the original cannot play, it is the only way to see anything.
         */
        current && current.uploaded && current.preview_state !== 'ready' ? (
          <Button
            size="sm"
            variant="secondary"
            disabled={request.isPending || current.preview_state === 'queued' || current.preview_state === 'running'}
            onClick={() => request.mutate()}
            title={
              canPlayOriginal
                ? 'Re-encodes a smaller copy. The original already plays, so this is only for a slow connection.'
                : 'This camera needs transcoding before any browser can play it.'
            }
          >
            {request.isPending ? 'Requesting…' : canPlayOriginal ? 'Make a smaller copy' : 'Generate preview'}
          </Button>
        ) : null
      }
    >
      {!episode ? (
        <p className="text-[13px] text-muted">Select an episode to load it here.</p>
      ) : media.isPending ? (
        <MicroLabel size="xs">Loading cameras…</MicroLabel>
      ) : media.isError ? (
        <ErrorState error={media.error} onRetry={() => void media.refetch()} title="Could not load this episode" />
      ) : !cameras.length ? (
        // Not an error: a dataset without a `video_path` in its metadata declares no video at all.
        // But "no video" is not the same as "nothing to look at" -- an MCAP recording *is* the
        // episode, and this used to be a dead end in front of a file that was right there.
        <RecordingOnly datasetId={datasetId} tenantId={tenantId} episode={episode} />
      ) : (
        <>
          {showing ? (
            <HlsPlayer
              // The variant is in the key on purpose: switching rendition swaps the source, and
              // hls.js cannot be re-pointed in place. Position and play state survive it the same
              // way they survive a camera switch — through the refs above, not through the element.
              key={`${episodeId}-${current!.video_key}-${showing}`}
              datasetId={datasetId}
              tenantId={tenantId}
              episodeId={episodeId!}
              videoKey={current!.video_key}
              variant={showing}
              videoRef={videoRef}
              onTime={setPositionMs}
              onPlayingChange={setPlaying}
              startAtMs={positionMs}
              autoPlay={playing}
            />
          ) : (
            <div className="grid aspect-video place-items-center rounded border border-border bg-inset text-center">
              <div className="grid justify-items-center gap-2 px-6">
                <MicroLabel size="xs">
                  {current?.uploaded === false
                    ? 'Declared but not uploaded'
                    : STATE_TEXT[current?.preview_state ?? 'none']}
                </MicroLabel>
                {current?.error_code ? (
                  <span className="font-mono text-[11px] text-danger">{current.error_code}</span>
                ) : null}
                {current?.uploaded === false ? (
                  <p className="max-w-sm text-[12px] leading-relaxed text-muted">
                    The dataset's metadata names a file for this camera, and no such file was uploaded.
                  </p>
                ) : null}
              </div>
            </div>
          )}

          {/*
            Only when the dataset actually carries annotations. Most do not, and an empty strip on
            every other dataset would be a permanent piece of furniture meaning nothing.

            It renders even when nothing is playing: the segments are a fact about the episode, and
            an operator deciding whether to spend a transcode is better off seeing what is in it
            first. `onSeek` is what is withheld — a strip with no player is a picture, not a control.
          */}
          {timeline?.segments.length ? (
            <AnnotationTimeline
              timeline={timeline}
              positionMs={positionMs}
              onSeek={
                current?.preview_state === 'ready'
                  ? ms => {
                      if (videoRef.current) videoRef.current.currentTime = ms / 1000;
                      setPositionMs(ms);
                    }
                  : undefined
              }
            />
          ) : null}

          {request.isError ? (
            <p className="mt-2 text-[12px] text-danger">
              {request.error instanceof ApiError ? request.error.message : 'Could not request a preview.'}
            </p>
          ) : null}

          {/*
            Two rungs, not a ladder. Each extra rung is another encode, so a YouTube-style ladder
            would make transcoding slower rather than faster; what makes it fast is not doing it.
            Only shown when both are actually available — a switch with one working position is a
            control that lies about having a choice.
          */}
          {canPlayOriginal && canPlayPreview ? (
            <div className="mt-3 flex items-center gap-1.5">
              <MicroLabel size="xs" tone="muted">
                Quality
              </MicroLabel>
              {(['original', 'preview'] as const).map(option => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setVariant(option)}
                  aria-pressed={showing === option}
                  className={cn(
                    'cursor-pointer rounded-[3px] border',
                    showing === option ? 'border-primary' : 'border-transparent'
                  )}
                  title={
                    option === 'original'
                      ? `The uploaded file, untouched${current?.width ? ` (${current.width}×${current.height})` : ''}`
                      : 'A smaller H.264 re-encode, for a slow connection'
                  }
                >
                  <Badge tone="neutral">{option}</Badge>
                </button>
              ))}
            </div>
          ) : null}

          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            {cameras.map(camera => (
              <button
                key={camera.video_key}
                type="button"
                onClick={() => setSelected(camera.video_key)}
                aria-pressed={camera.video_key === current?.video_key}
                // A camera whose file never arrived is still selectable: seeing why it will not
                // play is the point of listing it.
                //
                // Which camera is selected and whether its file arrived are two independent facts,
                // so they get two channels. They used to share one: selected was `primary` (#b9441f)
                // and missing was `danger` (#c94a3a) -- two reds on the same pill, and because
                // selection won the ternary, selecting a missing camera hid the fact that it was
                // missing. Selection is now the border, which is how the rest of the app says
                // "active" (ui/tab-row.tsx, ui/panel.tsx, domain/version-lineage.tsx).
                className={cn(
                  'cursor-pointer rounded-[3px] border',
                  camera.video_key === current?.video_key ? 'border-primary' : 'border-transparent'
                )}
                title={[
                  camera.video_key,
                  camera.width && camera.height ? `${camera.width}×${camera.height}` : null,
                  camera.fps ? `${camera.fps} fps` : null,
                  camera.source_codec
                ]
                  .filter(Boolean)
                  .join(' · ')}
              >
                <Badge tone={!camera.uploaded || camera.preview_state === 'failed' ? 'danger' : 'neutral'}>
                  {cameraLabel(camera.video_key)}
                </Badge>
              </button>
            ))}
          </div>
          {current ? (
            <MicroLabel size="xs" className="mt-2 block">
              {[
                current.width && current.height ? `${current.width}×${current.height}` : null,
                current.fps ? `${current.fps} fps` : null,
                current.source_codec ? `source ${current.source_codec}` : null,
                // Which one is actually on screen, rather than which ones exist.
                showing === 'original' ? 'playing the original' : showing === 'preview' ? 'playing the preview' : null
              ]
                .filter(Boolean)
                .join(' · ')}
            </MicroLabel>
          ) : null}
        </>
      )}
    </Panel>
  );
}

/**
 * An episode that is a recording rather than a video.
 *
 * MCAP carries timestamped messages -- camera topics, poses, control -- not a stream a browser can
 * decode, so there is nothing to transcode and nothing to put in a `<video>`. What there *is* is the
 * file itself, and the standard way to look inside one is Foxglove, which opens a remote MCAP from a
 * URL. The signed URL is minted on demand and is the same one a download uses, so opening a
 * recording is logged and audited exactly like taking a copy of it (INV-70).
 *
 * The alternative was the message this replaces: "there is nothing to play", in front of a file the
 * operator had just been asked to review.
 */
function RecordingOnly({ datasetId, tenantId, episode }: { datasetId: string; tenantId?: string; episode: Episode }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!episode.source_file_id)
    return (
      <p className="text-[13px] leading-relaxed text-muted">
        This dataset declares no video for its episodes, and this episode names no single file of its own — so there is
        nothing to open.
      </p>
    );

  // Signed on demand rather than up front: a URL minted for every episode on every render would be
  // a download log entry for a file nobody opened.
  const open = async (target: (url: string) => string) => {
    setBusy(true);
    setError('');
    try {
      const { url } = await api<{ url: string }>(
        `/datasets/${datasetId}/files/${episode.source_file_id}/download-url`,
        { tenantId }
      );
      window.open(target(url), '_blank', 'noopener,noreferrer');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not open this recording.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-3 rounded border border-border bg-inset px-5 py-6">
      <MicroLabel size="xs">Recording · no video stream</MicroLabel>
      <p className="max-w-lg text-[13px] leading-relaxed text-muted">
        This episode is an MCAP recording: timestamped messages rather than a video track, so there is nothing to
        transcode. Open it in Foxglove to inspect its topics, or take a copy.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          disabled={busy}
          onClick={() =>
            open(url => `https://app.foxglove.dev/~/view?ds=remote-file&ds.url=${encodeURIComponent(url)}`)
          }
        >
          {busy ? 'Signing…' : 'Open in Foxglove'}
        </Button>
        <Button size="sm" variant="secondary" disabled={busy} onClick={() => open(url => url)}>
          Download
        </Button>
      </div>
      {error ? <p className="text-[12px] text-danger">{error}</p> : null}
      <MicroLabel size="xs" tone="muted">
        The link is signed and expires; Foxglove reads it directly from object storage.
      </MicroLabel>
    </div>
  );
}

/**
 * Plays the single-file HLS the media lane produced.
 *
 * The playlist is fetched rather than pointed at, because its media line has to be rewritten to a
 * presigned URL server-side and the browser cannot mint one. It is handed to hls.js as a blob;
 * Safari, which plays HLS natively and does not run hls.js, gets the same blob through `src`.
 *
 * Presigned URLs last 900s, so a media error late in a long review is most likely an expiry: the
 * player re-fetches the playlist once rather than leaving a dead frame on screen.
 */
function HlsPlayer({
  datasetId,
  tenantId,
  episodeId,
  videoKey,
  variant,
  videoRef,
  onTime,
  onPlayingChange,
  startAtMs = 0,
  autoPlay = false
}: {
  datasetId: string;
  tenantId?: string;
  episodeId: string;
  videoKey: string;
  /** `original` streams the uploaded object; `preview` the transcoded HLS. */
  variant: Variant;
  /** Shared with the viewer, so a click on the annotation strip can seek this element. */
  videoRef?: RefObject<HTMLVideoElement | null>;
  onTime?: (ms: number) => void;
  onPlayingChange?: (playing: boolean) => void;
  /** Where the previous player had got to. Applied once, when this one knows its duration. */
  startAtMs?: number;
  autoPlay?: boolean;
}) {
  const own = useRef<HTMLVideoElement>(null);
  const ref = videoRef ?? own;
  const [attempt, setAttempt] = useState(0);
  /**
   * Where to pick up, and whether to keep playing, the next time a source is attached.
   *
   * Kept live rather than applied once, because a source is attached more than once: on mount after
   * a camera or rendition switch, and again whenever a presigned URL expires and has to be reissued.
   * A 900-second signature against a sixteen-minute episode expires mid-playback as a matter of
   * course, and re-signing used to be the same thing as jumping back to 00:00.
   */
  const resumeTo = useRef({ ms: startAtMs, playing: autoPlay });

  const playlist = useQuery({
    queryKey: ['admin', 'episode-playlist', datasetId, episodeId, videoKey, variant, attempt],
    queryFn: () =>
      api<{ playlist: string | null; source_url: string | null; poster_url: string | null }>(
        `/datasets/${datasetId}/episodes/${episodeId}/media/${encodeURIComponent(videoKey)}/playlist?variant=${variant}`,
        { tenantId }
      ),
    staleTime: 600_000
  });

  useEffect(() => {
    const video = ref.current;
    if (!video || !playlist.data) return;

    /**
     * The original is a plain MP4 the browser range-requests itself — no manifest, no hls.js, and
     * no second copy of the bytes in object storage. The preview is single-file HLS, which does
     * need the manifest rewritten to a signed URL, and so still goes through the blob.
     */
    if (playlist.data.source_url) {
      video.src = playlist.data.source_url;
      // The signature outlives a short episode and not a long one, and an expired one surfaces as a
      // media error on the element. Re-sign and resume, rather than leaving a dead frame.
      //
      // Bounded, because a source the browser genuinely cannot decode raises the same event: without
      // a ceiling that is a re-signing loop against an object that will never play.
      const reSign = () => setAttempt(n => (n < MAX_RESIGNS ? n + 1 : n));
      video.addEventListener('error', reSign);
      return () => video.removeEventListener('error', reSign);
    }
    if (!playlist.data.playlist) return;
    const url = URL.createObjectURL(new Blob([playlist.data.playlist], { type: 'application/vnd.apple.mpegurl' }));

    let hls: Hls | undefined;
    if (Hls.isSupported()) {
      hls = new Hls({ enableWorker: true });
      hls.loadSource(url);
      hls.attachMedia(video);
      hls.on(Hls.Events.ERROR, (_event, data) => {
        // Only a fatal network error is worth re-signing for; hls.js recovers the rest itself.
        if (data.fatal && data.type === Hls.ErrorTypes.NETWORK_ERROR) setAttempt(n => n + 1);
      });
    } else {
      video.src = url;
    }
    return () => {
      hls?.destroy();
      URL.revokeObjectURL(url);
    };
  }, [playlist.data]);

  /**
   * Pick up where the last source left off.
   *
   * On `loadedmetadata`, because a seek issued before the duration is known is silently discarded.
   * Once per attached source, not once per operator action: `applied` is local to this effect, so a
   * later seek is theirs to keep and re-signing gets a fresh chance to restore.
   *
   * `play()` can still be refused by the autoplay policy — the catch leaves the video paused on the
   * right frame instead of throwing.
   */
  useEffect(() => {
    const video = ref.current;
    if (!video || !playlist.data) return;
    let applied = false;
    const resume = () => {
      if (applied) return;
      applied = true;
      const target = resumeTo.current;
      if (target.ms > 0 && Number.isFinite(video.duration))
        video.currentTime = Math.min(target.ms / 1000, Math.max(0, video.duration - 0.05));
      if (target.playing) void video.play().catch(() => undefined);
    };
    video.addEventListener('loadedmetadata', resume);
    if (video.readyState >= 1) resume();
    return () => video.removeEventListener('loadedmetadata', resume);
  }, [ref, playlist.data]);

  /**
   * Report the playhead to whoever is drawing against it.
   *
   * `timeupdate` alone is not enough for two different reasons. It fires about four times a second,
   * which makes a marker visibly step rather than move; and it does not fire at all while a scrub
   * is in progress, which is precisely the case where somebody is watching to see what is there.
   * So: a frame loop while playing, and the events for everything else.
   *
   * Updates are quantised to a tenth of a second. At 60 Hz this is a state change per frame through
   * a component drawing up to a couple of hundred spans; at 10 Hz it is smooth and six times
   * cheaper, and no one can see the difference on a 2.5px-tall strip.
   */
  useEffect(() => {
    const video = ref.current;
    if (!video || !onTime) return;
    let frame = 0;
    let last = -1;
    const report = () => {
      const tenths = Math.round(video.currentTime * 10);
      resumeTo.current = { ms: tenths * 100, playing: !video.paused && !video.ended };
      if (tenths === last) return;
      last = tenths;
      onTime(tenths * 100);
    };
    const loop = () => {
      report();
      frame = requestAnimationFrame(loop);
    };
    const start = () => {
      resumeTo.current.playing = true;
      onPlayingChange?.(true);
      if (!frame) frame = requestAnimationFrame(loop);
    };
    const stop = () => {
      resumeTo.current.playing = false;
      onPlayingChange?.(false);
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
      report();
    };
    video.addEventListener('play', start);
    video.addEventListener('playing', start);
    video.addEventListener('pause', stop);
    video.addEventListener('ended', stop);
    video.addEventListener('seeking', report);
    video.addEventListener('seeked', report);
    video.addEventListener('timeupdate', report);
    video.addEventListener('loadedmetadata', report);
    if (!video.paused) start();
    return () => {
      if (frame) cancelAnimationFrame(frame);
      video.removeEventListener('play', start);
      video.removeEventListener('playing', start);
      video.removeEventListener('pause', stop);
      video.removeEventListener('ended', stop);
      video.removeEventListener('seeking', report);
      video.removeEventListener('seeked', report);
      video.removeEventListener('timeupdate', report);
      video.removeEventListener('loadedmetadata', report);
    };
  }, [onTime, onPlayingChange, ref]);

  if (playlist.isError)
    return (
      <ErrorState error={playlist.error} onRetry={() => setAttempt(n => n + 1)} title="Could not load the video" />
    );

  return (
    <video
      ref={ref}
      controls
      preload="metadata"
      poster={playlist.data?.poster_url ?? undefined}
      className="aspect-video w-full rounded border border-border bg-inset"
    />
  );
}
